/**
 * @fileoverview Authentication context for global auth state management
 * Handles user authentication, session management, and security
 * @author ISCRI Team
 */

import { createContext, useReducer, useEffect, useCallback } from "react";
import { /*authApi,*/ parseAuthError } from "@services/authApi";
import {
  encryptUserId,
  decryptUserId,
  generateSessionToken,
  isValidEncryptedId,
} from "@utils/crypto";

/**
 * Authentication action types
 */
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  RESTORE_SESSION: "RESTORE_SESSION",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
};

/**
 * Initial authentication state with default values
 * Loading starts as true for session restoration check
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  sessionToken: null,
};

/**
 * Authentication reducer for state management
 * @param {Object} state - Current authentication state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New authentication state
 */
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null,
        sessionToken: action.payload.sessionToken,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload.error,
        sessionToken: null,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        sessionToken: null,
      };

    case AUTH_ACTIONS.RESTORE_SESSION:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        sessionToken: action.payload.sessionToken,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    default:
      return state;
  }
};

/**
 * Authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext(null);

/**
 * Session storage keys
 */
const STORAGE_KEYS = {
  IS_AUTHENTICATED: "isAuthenticated",
  AUTH_EXPIRY: "authExpiry",
  ENCRYPTED_USER_ID: "encryptedUserId",
  USERNAME: "username",
  SESSION_TOKEN: "sessionToken",
};

/**
 * Authentication context provider component
 * Manages auth state, session persistence, and security with encryption
 * Handles login/logout, session restoration, and token management
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that need auth context
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Saves authentication data to session storage with encryption
   * Encrypts user ID for security before storage
   * @param {Object} userData - User data to save
   * @param {string} sessionToken - Session token
   * @param {number} expiryTime - Expiration timestamp in milliseconds
   * @returns {void}
   */
  const saveAuthData = useCallback((userData, sessionToken, expiryTime) => {
    try {
      const encryptedId = encryptUserId(userData.id);

      sessionStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, "true");
      sessionStorage.setItem(STORAGE_KEYS.AUTH_EXPIRY, expiryTime.toString());
      sessionStorage.setItem(STORAGE_KEYS.ENCRYPTED_USER_ID, encryptedId);
      sessionStorage.setItem(STORAGE_KEYS.USERNAME, userData.username);
      sessionStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, sessionToken);
    } catch (error) {
      console.error("Failed to save auth data:", error);
    }
  }, []);

  /**
   * Clear authentication data from session storage
   */
  const clearAuthData = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
    });
  }, []);

  /**
   * Restores user session from storage with validation
   * Checks expiry, validates encrypted data, and handles cleanup on failure
   * @returns {Object|null} Restored session data with user and token, or null if invalid
   */
  const restoreSession = useCallback(() => {
    try {
      const isAuthenticated = sessionStorage.getItem(
        STORAGE_KEYS.IS_AUTHENTICATED
      );
      const authExpiry = sessionStorage.getItem(STORAGE_KEYS.AUTH_EXPIRY);
      const encryptedUserId = sessionStorage.getItem(
        STORAGE_KEYS.ENCRYPTED_USER_ID
      );
      const username = sessionStorage.getItem(STORAGE_KEYS.USERNAME);
      const sessionToken = sessionStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);

      if (!isAuthenticated || !authExpiry || !encryptedUserId || !username) {
        return null;
      }

      const now = Date.now();
      const expiry = parseInt(authExpiry, 10);

      if (now >= expiry) {
        clearAuthData();
        return null;
      }

      if (!isValidEncryptedId(encryptedUserId)) {
        clearAuthData();
        return null;
      }

      const decryptedUserId = decryptUserId(encryptedUserId);

      return {
        user: {
          id: decryptedUserId,
          username,
          encryptedId: encryptedUserId,
        },
        sessionToken,
      };
    } catch (error) {
      console.error("Failed to restore session:", error);
      clearAuthData();
      return null;
    }
  }, [clearAuthData]);

  /**
   * Authenticates a user using either admin or visitor credentials.
   *
   * Accepted credential pairs:
   *  - Admin:   VITE_APP_EMAIL + VITE_APP_PSWD
   *  - Visitor: VITE_APP_EMAIL_VISITOR + VITE_APP_PSWD_VISITOR
   *
   * On success:
   *  - Creates a session token
   *  - Sets a 2-hour expiration
   *  - Saves authentication data (user, token, expiry)
   *  - Dispatches LOGIN_SUCCESS with encrypted user ID
   *
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<boolean>} Success status
   */
  const login = useCallback(
    async (credentials) => {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      const adminEmail = window._env_?.VITE_APP_EMAIL;
      const adminPswd = window._env_?.VITE_APP_PSWD;
      const visitorEmail = window._env_?.VITE_APP_EMAIL_VISITOR;
      const visitorPswd = window._env_?.VITE_APP_PSWD_VISITOR;
      try {
        let userData = null;
        if (credentials.email === adminEmail && credentials.password === adminPswd) {
          userData = {
            id: "user_001",
            username: "ISCRI Admin",
          };
        }
        else if (credentials.email === visitorEmail && credentials.password === visitorPswd) {
          userData = {
            id: "user_temp_001",
            username: "ISCRI Visitor",
          };
        }
        else {
          throw new Error("Invalid email or password");
        }
        const sessionToken = generateSessionToken();
        const expiryTime = Date.now() + 2 * 60 * 60 * 1000;

        saveAuthData(userData, sessionToken, expiryTime);
        
        dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: {
                ...userData,
                encryptedId: encryptUserId(userData.id),
              },
              sessionToken,
            },
          });

          return true;

      } catch (error) {
        const parsedError = parseAuthError(error);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: parsedError.message },
        });

        return false;
      }
    },
    [saveAuthData]
  );

  /**
   * Logout current user
   */
  const logout = useCallback(async () => {
    try {
      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    } catch (error) {
      console.error("Logout error:", error);

      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, [clearAuthData]);

  /**
   * Check current authentication status
   * @returns {boolean} Authentication status
   */
  const checkAuthStatus = useCallback(() => {
    const sessionData = restoreSession();

    if (sessionData) {
      dispatch({
        type: AUTH_ACTIONS.RESTORE_SESSION,
        payload: sessionData,
      });
      return true;
    }

    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    return false;
  }, [restoreSession]);

  /**
   * Get current user information
   * @returns {Object|null} Current user data
   */
  const getCurrentUser = useCallback(() => {
    if (!state.isAuthenticated || !state.user) {
      return null;
    }

    return {
      id: state.user.id,
      username: state.user.username,
      encryptedId: state.user.encryptedId,
    };
  }, [state.isAuthenticated, state.user]);

  /**
   * Clear authentication error
   */
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  /**
   * Refresh authentication token
   * @returns {Promise<boolean>} Success status
   */
  const refreshToken = useCallback(async () => {
    try {
      return true;
      // eslint-disable-next-line no-unreachable
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return false;
    }
  }, [logout]);

  /**
   * Initialize authentication on mount
   */
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  /**
   * Context value object
   */
  const contextValue = {
    // State
    user: getCurrentUser(),
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,

    // Actions
    login,
    logout,
    checkAuthStatus,
    clearError,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
